import {
  addDays,
  addMinutes,
  areIntervalsOverlapping,
  compareAsc,
  startOfToday,
  startOfTomorrow,
} from 'date-fns';
import { defineStore } from 'pinia';

import {
  MilestoneEntity,
  OrderDependencyEntity,
  OrderEntity,
  PauseEntity,
  TradeEntity,
  WbsSectionEntity,
} from '@/common/types/entities';
import { Collision } from '@/features/collisions/types';
import {
  getSectionAncestors,
  getSectionChildrenFromMap,
  getSectionChildrenMap,
  sortSectionChildrenMap,
} from '@/features/projectStructure/utils/sectionTreeUtils';
import { ScheduleAlert } from '@/features/scheduleAlerts/types';
import {
  ExportEntities,
  ExportKey,
  ExportSettingsInput,
  ExportStatus,
} from '@/features/scheduleExport/types';
import { copy, isEqual } from '@/helpers/utils/objects';

import { getInitialExportSettings } from './scheduleExportUtils';

export type ScheduleExportStore = ReturnType<typeof useScheduleExportStore>;

export const useScheduleExportStore = defineStore('schedule-export-store', () => {
  const exportDialogOpen = ref(false);
  const exportStatus = ref<ExportStatus>('WAITING');

  // Internal timer reference to auto-transition from SUCCESS to WAITING
  let successTimeoutId: number | null = null;

  const exportCollisions = ref<Map<string, Collision>>(new Map());
  const exportDependencies = ref<Map<string, OrderDependencyEntity>>(new Map());
  const exportMilestones = ref<Map<string, MilestoneEntity>>(new Map());
  const exportOrders = ref<Map<string, OrderEntity>>(new Map());
  const exportPauses = ref<Map<string, PauseEntity>>(new Map());
  const exportScheduleAlerts = ref<Map<string, ScheduleAlert>>(new Map());
  const exportTrades = ref<Map<string, TradeEntity>>(new Map());
  const exportWbsSections = ref<Map<string, WbsSectionEntity>>(new Map());
  const isHourlyProject = ref(false);

  const previewLoading = ref(true);

  const initialExportSettings = getInitialExportSettings();

  const advancedSettings = ref<ExportSettingsInput['advancedSettings']>(
    initialExportSettings.advancedSettings,
  );
  const collapsedRowIds = ref<ExportSettingsInput['collapsedRowIds']>(
    initialExportSettings.collapsedRowIds,
  );
  const contributorGroups = ref<ExportSettingsInput['contributorGroups']>(
    initialExportSettings.contributorGroups,
  );
  const eventColorType = ref<ExportSettingsInput['eventColorType']>(
    initialExportSettings.eventColorType,
  );
  const logoSrc = ref<ExportSettingsInput['logoSrc']>(initialExportSettings.logoSrc);
  const printerSettings = ref<ExportSettingsInput['printerSettings']>(
    initialExportSettings.printerSettings,
  );
  const sections = ref<ExportSettingsInput['sections']>(initialExportSettings.sections);
  const totalSections = ref<ExportSettingsInput['totalSections']>(
    initialExportSettings.totalSections,
  );
  const tenantTradeVariations = ref<ExportSettingsInput['tenantTradeVariations']>(
    initialExportSettings.tenantTradeVariations,
  );
  const timeRange = ref<ExportSettingsInput['timeRange']>(initialExportSettings.timeRange);

  const selectedExportType = ref<ExportKey>(ExportKey.PDF);

  const isReadyToExport = computed(() => exportStatus.value !== 'IN_PROGRESS');

  const exportWbsSectionsChildren = computed(() =>
    sortSectionChildrenMap(getSectionChildrenMap(exportWbsSections.value)),
  );

  // eslint-disable-next-line complexity
  const updateExportSettings = (settings: Partial<ExportSettingsInput>) => {
    let atLeastOneSettingChanged = false;
    if (
      settings.advancedSettings !== undefined &&
      !isEqual(settings.advancedSettings, advancedSettings.value)
    ) {
      advancedSettings.value = copy(settings.advancedSettings);
      atLeastOneSettingChanged = true;
    }
    if (
      settings.collapsedRowIds !== undefined &&
      !isEqual(settings.collapsedRowIds, collapsedRowIds.value)
    ) {
      collapsedRowIds.value = new Set(settings.collapsedRowIds);
      atLeastOneSettingChanged = true;
    }
    if (
      settings.contributorGroups !== undefined &&
      !isEqual(settings.contributorGroups, contributorGroups.value)
    ) {
      contributorGroups.value = Array.from(settings.contributorGroups);
      atLeastOneSettingChanged = true;
    }
    if (settings.eventColorType !== undefined && settings.eventColorType !== eventColorType.value) {
      eventColorType.value = settings.eventColorType;
      atLeastOneSettingChanged = true;
    }
    if (settings.logoSrc !== undefined && settings.logoSrc !== logoSrc.value) {
      logoSrc.value = settings.logoSrc;
      atLeastOneSettingChanged = true;
    }
    if (
      settings.printerSettings !== undefined &&
      !isEqual(settings.printerSettings, printerSettings.value)
    ) {
      printerSettings.value = copy(settings.printerSettings);
      atLeastOneSettingChanged = true;
    }
    if (settings.sections !== undefined && !isEqual(settings.sections, sections.value)) {
      sections.value = Array.from(settings.sections);
      atLeastOneSettingChanged = true;
    }
    if (settings.totalSections !== undefined && settings.totalSections !== totalSections.value) {
      totalSections.value = settings.totalSections;
      atLeastOneSettingChanged = true;
    }
    if (
      settings.tenantTradeVariations !== undefined &&
      !isEqual(settings.tenantTradeVariations, tenantTradeVariations.value)
    ) {
      tenantTradeVariations.value = Array.from(settings.tenantTradeVariations);
      atLeastOneSettingChanged = true;
    }
    if (settings.timeRange !== undefined && !isEqual(settings.timeRange, timeRange.value)) {
      timeRange.value = settings.timeRange
        ? {
            type: 'absolute',
            start: new Date(settings.timeRange.start),
            end: new Date(settings.timeRange.end),
          }
        : null;
      atLeastOneSettingChanged = true;
    }
    return atLeastOneSettingChanged;
  };

  const getExportSettings = (): ExportSettingsInput => {
    return {
      advancedSettings: advancedSettings.value,
      collapsedRowIds: collapsedRowIds.value,
      contributorGroups: contributorGroups.value,
      eventColorType: eventColorType.value,
      logoSrc: logoSrc.value,
      printerSettings: printerSettings.value,
      sections: sections.value,
      totalSections: totalSections.value,
      tenantTradeVariations: tenantTradeVariations.value,
      timeRange: timeRange.value,
    };
  };

  const setIsHourlyProject = (isHourly: boolean) => {
    isHourlyProject.value = isHourly;
  };

  const createSnapshot = ({
    collisions,
    scheduleAlerts,
    dependencies,
    milestones,
    orders,
    pauses,
    trades,
    wbsSections,
  }: ExportEntities) => {
    exportCollisions.value = new Map(collisions);
    exportDependencies.value = new Map(dependencies);
    exportMilestones.value = new Map(milestones);
    exportOrders.value = new Map(orders);
    exportPauses.value = new Map(pauses);
    exportScheduleAlerts.value = new Map(scheduleAlerts);
    exportTrades.value = new Map(trades);
    exportWbsSections.value = new Map(wbsSections);
    exportDialogOpen.value = true;
  };

  const closeExportDialog = () => {
    exportDialogOpen.value = false;
  };

  const openExportDialog = () => {
    exportDialogOpen.value = true;
  };

  const reset = () => {
    exportDependencies.value = new Map();
    exportMilestones.value = new Map();
    exportOrders.value = new Map();
    exportPauses.value = new Map();
    exportTrades.value = new Map();
    exportWbsSections.value = new Map();
    isHourlyProject.value = false;
    exportDialogOpen.value = false;
    updateExportSettings(getInitialExportSettings());
    selectedExportType.value = ExportKey.PDF;
  };

  const setExportStatus = (status: ExportStatus) => {
    if (successTimeoutId !== null) {
      window.clearTimeout(successTimeoutId);
      successTimeoutId = null;
    }

    exportStatus.value = status;

    if (status === 'SUCCESS') {
      successTimeoutId = window.setTimeout(() => {
        exportStatus.value = 'WAITING';
        successTimeoutId = null;
      }, 2000);
    }
  };

  const filteredEntities = computed(() => {
    const exportOrdersList = Array.from(exportOrders.value.values());
    const exportWbsSectionsList = Array.from(exportWbsSections.value.values());
    const exportMilestonesList = Array.from(exportMilestones.value.values());

    const filteredUniqueContributorGroups = new Set(contributorGroups.value);
    const filteredUniqueTrades = new Set(tenantTradeVariations.value);
    const filteredUniqueSections = new Set(sections.value);
    const filteredTimeRange = timeRange.value;

    const filteredUniqueSectionsWithParents = new Set<string>();

    filteredUniqueSections.forEach((sectionId) => {
      const section = exportWbsSections.value.get(sectionId);
      if (!section) return;

      filteredUniqueSectionsWithParents.add(section.id);
      if (!section.parentId) return;
      if (filteredUniqueSectionsWithParents.has(section.parentId)) return;
      const sectionAncestors = getSectionAncestors(exportWbsSections.value, section);
      sectionAncestors.forEach((section) => filteredUniqueSectionsWithParents.add(section.id));
    });

    const hiddenSectionsDueToCollapsing = new Set<string>();

    collapsedRowIds.value.forEach((rowId) => {
      const section = exportWbsSections.value.get(rowId);
      if (!section) return;
      const sectionChildren = getSectionChildrenFromMap(
        section,
        exportWbsSections.value,
        exportWbsSectionsChildren.value,
      );
      sectionChildren.forEach((section) => {
        hiddenSectionsDueToCollapsing.add(section.id);
      });
    });

    const filteredOrders = exportOrdersList.filter((order) => {
      const matchesContributorGroups =
        filteredUniqueContributorGroups.size === 0 ||
        filteredUniqueContributorGroups.has(order.contributorGroup?.id ?? '');

      const matchesTimeRange =
        !filteredTimeRange ||
        areIntervalsOverlapping({ start: order.startAt, end: order.finishAt }, filteredTimeRange);

      const matchesTrades =
        filteredUniqueTrades.size === 0 || filteredUniqueTrades.has(order.tenantTradeVariation.id);

      const matchesSections =
        filteredUniqueSections.size === 0 || filteredUniqueSections.has(order.wbsSection.id);

      return matchesContributorGroups && matchesTimeRange && matchesTrades && matchesSections;
    });

    const displayedOrders = filteredOrders.filter((order) => {
      if (hiddenSectionsDueToCollapsing.size === 0) return true;
      return !hiddenSectionsDueToCollapsing.has(order.wbsSection.id);
    });

    const filteredMilestones = exportMilestonesList.filter((milestone) => {
      const matchesContributorGroups =
        filteredUniqueContributorGroups.size === 0 ||
        filteredUniqueContributorGroups.has(milestone.contributorGroup?.id ?? '');

      const matchesTimeRange =
        !filteredTimeRange ||
        areIntervalsOverlapping({ start: milestone.date, end: milestone.date }, filteredTimeRange);

      const matchesTrades =
        filteredUniqueTrades.size === 0 ||
        filteredUniqueTrades.has(milestone.tenantTradeVariation?.id ?? '');

      const matchesSections =
        !milestone.wbsSection ||
        filteredUniqueSections.size === 0 ||
        filteredUniqueSections.has(milestone.wbsSection.id);

      return matchesContributorGroups && matchesTimeRange && matchesTrades && matchesSections;
    });

    const orders = new Map(filteredOrders.map((order) => [order.id, order]));
    const milestones = new Map(filteredMilestones.map((milestone) => [milestone.id, milestone]));

    const sectionsWithEvents = new Set<string>();

    const shouldFilterSectionsWithoutEvents =
      filteredUniqueContributorGroups.size > 0 ||
      filteredUniqueTrades.size > 0 ||
      filteredTimeRange !== null;

    if (shouldFilterSectionsWithoutEvents) {
      [...filteredOrders, ...filteredMilestones].forEach((event) => {
        if (!event.wbsSection) return;
        if (sectionsWithEvents.has(event.wbsSection.id)) return;

        const section = exportWbsSections.value.get(event.wbsSection.id);

        if (!section) return;

        const sectionAncestors = getSectionAncestors(exportWbsSections.value, section, true);

        sectionAncestors.forEach((section) => sectionsWithEvents.add(section.id));
      });
    }

    const filteredSections = exportWbsSectionsList.filter((section) => {
      const matchesSections =
        filteredUniqueSectionsWithParents.size === 0 ||
        filteredUniqueSectionsWithParents.has(section.id ?? '');

      if (!shouldFilterSectionsWithoutEvents) return matchesSections;

      return matchesSections && sectionsWithEvents.has(section.id);
    });

    const wbsSections = new Map(filteredSections.map((section) => [section.id, section]));
    return {
      displayedOrders: new Map(displayedOrders.map((order) => [order.id, order])),
      orders,
      milestones,
      wbsSections,
    };
  });

  const exportDateRange = computed(() => {
    if (timeRange.value) {
      return {
        startDate: new Date(timeRange.value.start),
        endDate: new Date(timeRange.value.end),
      };
    }
    const orderDates = Array.from(filteredEntities.value.orders.values())
      .map(({ startAt, finishAt, dryingBreak }) => {
        const finishAtDate = dryingBreak ? addMinutes(finishAt, dryingBreak.duration) : finishAt;

        return [startAt, finishAtDate];
      })
      .flat();

    const sectionFilterApplied = sections.value.length !== 0;

    let orderedDates = orderDates;

    if (sectionFilterApplied) {
      const milestoneDates = Array.from(filteredEntities.value.milestones.values())
        .filter(({ wbsSection }) => Boolean(wbsSection))
        .map(({ date }) => date);
      orderedDates = orderedDates.concat(milestoneDates);
    } else {
      const milestoneDates = Array.from(filteredEntities.value.milestones.values()).map(
        ({ date }) => date,
      );
      orderedDates = orderedDates.concat(milestoneDates);
    }

    orderedDates = orderedDates.sort(compareAsc);

    return {
      startDate: orderedDates[0] ?? startOfToday(),
      endDate: addDays(orderedDates[orderedDates.length - 1] ?? startOfTomorrow(), 1),
    };
  });

  return {
    exportDateRange,
    exportDependencies,
    exportDialogOpen,
    exportCollisions,
    exportMilestones,
    exportOrders,
    exportPauses,
    exportScheduleAlerts,
    previewLoading,
    advancedSettings,
    collapsedRowIds,
    contributorGroups,
    eventColorType,
    logoSrc,
    printerSettings,
    sections,
    totalSections,
    tenantTradeVariations,
    timeRange,
    exportStatus,
    exportTrades,
    exportWbsSections,
    selectedExportType,
    isReadyToExport,
    filteredEntities,
    closeExportDialog,
    createSnapshot,
    openExportDialog,
    reset,
    updateExportSettings,
    getExportSettings,
    setExportStatus,
    isHourlyProject,
    setIsHourlyProject,
  };
});
